import { useUpdateLocationAlarm } from '@hakimo-ui/hakimo/data-access';
import { TwilioLogType } from '@hakimo-ui/hakimo/types';
import {
  draggableNodeAtom,
  eventTracker,
  toast,
  useSOPWorkflowState,
} from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { PhoneIcon } from '@heroicons/react/24/solid';
import { Call } from '@twilio/voice-sdk';
import { useSetAtom } from 'jotai';
import { useAlarmGroupStatic } from '../../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { DraggableCallView } from './DraggableCallView';

interface Props {
  toNumber: string;
  toName: string;
  tag?: string;
  locationAlarmId?: string;
  locationTenantId?: string;
  locationId?: number;
  productType?: 'scan' | 'remote-guarding';
  onCallEnd?: () => void;
}

export function VoiceCall(props: Props) {
  const {
    toNumber,
    toName,
    tag,
    locationAlarmId,
    locationTenantId,
    locationId,
    productType,
    onCallEnd,
  } = props;

  const setDraggableNode = useSetAtom(draggableNodeAtom);
  const { dataVersion } = useAlarmGroupStatic();

  const mutation = useUpdateLocationAlarm(locationAlarmId ?? '', {
    productType,
    dataVersion: productType === 'scan' ? undefined : dataVersion,
  });

  const { logCallFromSOP } = useSOPWorkflowState();

  const onCallInitiated = (c: Call) => {
    if (locationAlarmId && c.parameters['CallSid']) {
      const twilioCallSid = c.parameters['CallSid'];

      logCallFromSOP?.(twilioCallSid, tag);

      mutation.mutate({
        twilio_log_type: TwilioLogType.TWILIO_CALLS,
        twilio_ssid: twilioCallSid,
        twilio_calling_to_name: toName,
      });
    }
  };

  const onCallDisconnected = () => {
    if (locationAlarmId) onCallEnd?.();
  };

  const onCall = () => {
    if (!toNumber || toNumber.trim() === '-') {
      toast('No phone number available to make a call', { type: 'error' });
      return;
    }

    setDraggableNode(
      <DraggableCallView
        onCloseCb={() => setDraggableNode(null)}
        toName={toName}
        toNumber={toNumber}
        locationTenantId={locationTenantId}
        locationId={locationId}
        onCallInitiated={onCallInitiated}
        onCallDisconnected={onCallDisconnected}
      />
    );
  };

  return (
    <Button
      variant="icon"
      onClick={onCall}
      onSideEffect={eventTracker('call_apartment_contact')}
      title={`Call ${toName}`}
    >
      <PhoneIcon className="text-primary-500 h-4 w-4" />
    </Button>
  );
}

export default VoiceCall;
